import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Edit2, Trash2, Save, Layers, Castle, Image as ImageIcon, Eye, X, MapPinned } from 'lucide-react';
import RpgCityMapEditor from './RpgCityMapEditor';
import RpgCropManager from './RpgCropManager';
import RpgNpcManager from './RpgNpcManager';

const TILESET_CATEGORIES = [
  { value: 'terrain', label: 'Terreno' },
  { value: 'objects', label: 'Objetos' },
  { value: 'props', label: 'Decoracion' },
  { value: 'buildings', label: 'Edificios' },
  { value: 'resources', label: 'Recursos' },
  { value: 'characters', label: 'Personajes' }
];

const CATALOG_CATEGORIES = [
  { value: 'terrain', label: 'Suelo' },
  { value: 'walls', label: 'Muros' },
  { value: 'buildings', label: 'Edificios' },
  { value: 'props', label: 'Decoracion' },
  { value: 'objects', label: 'Objetos' },
  { value: 'resources', label: 'Recursos' },
  { value: 'characters', label: 'Personajes' },
  { value: 'npc', label: 'NPC' },
  { value: 'general', label: 'General' }
];

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || `rpg-${Date.now()}`;
}

function parseJsonText(value) {
  if (!value) return {};
  try {
    return JSON.parse(value);
  } catch (_) {
    return { raw: String(value) };
  }
}

function normalizeCatalogEntry(entry) {
  return {
    name: String(entry?.name || '').trim(),
    category: String(entry?.category || 'general').trim() || 'general',
    tile_index: Number(entry?.tile_index) || 0,
    blocked: entry?.blocked === false ? 0 : 1,
    description: String(entry?.description || '').trim()
  };
}

function inferCatalogCategory(index, total, baseCategory) {
  const normalizedBase = String(baseCategory || 'general').trim() || 'general';
  if (normalizedBase !== 'terrain' && normalizedBase !== 'general') {
    return normalizedBase;
  }

  const ratio = total > 0 ? index / total : 0;
  if (ratio < 0.55) return 'terrain';
  if (ratio < 0.70) return 'props';
  if (ratio < 0.85) return 'buildings';
  if (ratio < 0.95) return 'objects';
  return 'resources';
}

function normalizeTilesetForm(form) {
  const metadata = parseJsonText(form.metadata);
  return {
    id: form.id || null,
    slug: String(form.slug || slugify(form.name)).trim(),
    name: String(form.name || '').trim(),
    category: String(form.category || 'terrain'),
    image: String(form.image || '').trim() || null,
    tile_size: Number(form.tile_size) || 48,
    columns_count: Number(form.columns_count) || 0,
    metadata: {
      ...metadata,
      catalog: Array.isArray(form.catalog) ? form.catalog.map(normalizeCatalogEntry) : (Array.isArray(metadata.catalog) ? metadata.catalog.map(normalizeCatalogEntry) : [])
    },
    is_active: form.is_active === false ? 0 : 1
  };
}

function normalizeCityConfigForm(form) {
  return {
    city_id: Number(form.city_id) || 0,
    dome_theme: String(form.dome_theme || 'neutral').trim() || 'neutral',
    background_image: String(form.background_image || '').trim() || null,
    metadata: parseJsonText(form.metadata)
  };
}

function resolveImageSrc(image) {
  if (!image) return '';
  if (/^https?:\/\//i.test(image) || image.startsWith('/')) return image;
  const apiBaseUrl = import.meta.env.VITE_API_URL || '';
  return `${apiBaseUrl}/uploads/gallery/${image}`;
}

function loadImageSize(imageSrc) {
  return new Promise((resolve) => {
    if (!imageSrc) {
      resolve({ width: 0, height: 0 });
      return;
    }

    const img = new window.Image();
    img.onload = () => resolve({ width: img.naturalWidth || 0, height: img.naturalHeight || 0 });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = imageSrc;
  });
}

function buildAutoCatalogEntries({ name, category, tileSize, columnsCount, imageWidth, imageHeight }) {
  const tileSizeValue = Math.max(1, Number(tileSize) || 48);
  const columns = Math.max(1, Number(columnsCount) || Math.floor((Number(imageWidth) || tileSizeValue) / tileSizeValue) || 1);
  const rows = Math.max(1, Math.floor((Number(imageHeight) || tileSizeValue) / tileSizeValue) || 1);
  const total = Math.max(1, columns * rows);
  const baseName = String(name || 'Tile').trim() || 'Tile';
  const baseCategory = String(category || 'general').trim() || 'general';

  return Array.from({ length: total }).map((_, index) => ({
    name: `${baseName} ${index + 1}`,
    category: inferCatalogCategory(index, total, baseCategory),
    tile_index: index,
    blocked: 0,
    description: ''
  }));
}

function dedupeCatalogEntries(catalog) {
  const seen = new Set();
  return catalog.filter((entry) => {
    const key = `${String(entry.category || 'general').trim() || 'general'}:${Number(entry.tile_index) || 0}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function sortCatalogEntries(catalog) {
  const order = new Map(CATALOG_CATEGORIES.map((entry, index) => [entry.value, index]));
  return [...catalog].sort((a, b) => {
    const aCategory = String(a.category || 'general').trim() || 'general';
    const bCategory = String(b.category || 'general').trim() || 'general';
    const categoryDiff = (order.get(aCategory) ?? 99) - (order.get(bCategory) ?? 99);
    if (categoryDiff !== 0) return categoryDiff;
    const indexDiff = (Number(a.tile_index) || 0) - (Number(b.tile_index) || 0);
    if (indexDiff !== 0) return indexDiff;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function emptyTilesetForm() {
  return {
    id: null,
    slug: '',
    name: '',
    category: 'terrain',
    image: '',
    tile_size: 48,
    columns_count: 0,
    metadata: '{}',
    catalog: [],
    is_active: true
  };
}

function emptyCityConfigForm(cityId = '') {
  return {
    city_id: cityId ? String(cityId) : '',
    dome_theme: 'neutral',
    background_image: '',
    metadata: '{}'
  };
}

function normalizeCollection(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.cities)) return value.cities;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === 'object') {
    const objectValues = Object.values(value).filter(Boolean);
    if (objectValues.every((entry) => entry && typeof entry === 'object')) {
      return objectValues;
    }
  }
  return [];
}

function getCityTypeLabel(value) {
  const normalized = Number(value) || 0;
  if (normalized === 3) return 'Capital';
  if (normalized === 2) return 'Ciudad';
  if (normalized === 1) return 'Pueblo';
  return 'Neutral';
}

function RpgWorldManager({
  tilesets = [],
  cities = [],
  cityConfigs = [],
  npcTemplates = [],
  npcInstances = [],
  cropTransformations = [],
  items = [],
  playerAppearance = null,
  onSaveTileset,
  onDeleteTileset,
  onSaveCity,
  onDeleteCity,
  onSaveCityMap,
  onSaveNpcTemplate,
  onDeleteNpcTemplate,
  onSaveNpcInstance,
  onDeleteNpcInstance,
  onSaveCropTransformation,
  onDeleteCropTransformation,
  onSavePlayerAppearance,
  onOpenImageGallery,
  isSavingTileset = false,
  isSavingCity = false,
  isSavingCityMap = false,
  isSavingNpcTemplate = false,
  isSavingNpcInstance = false,
  isSavingCropTransformation = false,
  isSavingPlayerAppearance = false
}, ref) {
  const [activeSection, setActiveSection] = useState('tilesets');
  const [tilesetForm, setTilesetForm] = useState(emptyTilesetForm());
  const [cityConfigForm, setCityConfigForm] = useState(emptyCityConfigForm());
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedMapCityId, setSelectedMapCityId] = useState('');
  const [previewTileset, setPreviewTileset] = useState(null);
  const npcManagerRef = React.useRef(null);
  const normalizedTilesets = useMemo(() => normalizeCollection(tilesets), [tilesets]);
  const normalizedCities = useMemo(() => normalizeCollection(cities), [cities]);
  const normalizedCityConfigs = useMemo(() => normalizeCollection(cityConfigs), [cityConfigs]);

  useImperativeHandle(ref, () => ({
    setTilesetImage: (image) => {
      setTilesetForm((current) => ({ ...current, image }));
    },
    setCityBackgroundImage: (image) => {
      setCityConfigForm((current) => ({ ...current, background_image: image }));
    },
    setNpcSpriteImage: (image) => {
      npcManagerRef.current?.setNpcSpriteImage?.(image);
    },
    setPlayerSpriteImage: (image) => {
      npcManagerRef.current?.setPlayerSpriteImage?.(image);
    }
  }), []);

  const sortedTilesets = useMemo(() => {
    return [...normalizedTilesets].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [normalizedTilesets]);

  const sortedCities = useMemo(() => {
    return [...normalizedCities].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [normalizedCities]);

  const cityConfigById = useMemo(() => {
    const map = new Map();
    normalizedCityConfigs.forEach((entry) => {
      map.set(Number(entry.city_id || entry.city?.id || 0), entry);
    });
    return map;
  }, [normalizedCityConfigs]);

  const configuredCityEntries = useMemo(() => {
    return sortedCities.reduce((entries, city) => {
      const configEntry = cityConfigById.get(Number(city.id));
      if (configEntry?.config) {
        entries.push({
          city,
          configEntry
        });
      }
      return entries;
    }, []);
  }, [cityConfigById, sortedCities]);

  const configuredCities = useMemo(() => configuredCityEntries.map((entry) => entry.city), [configuredCityEntries]);

  const selectedCity = useMemo(() => {
    if (!sortedCities.length) return null;
    return sortedCities.find((city) => Number(city.id) === Number(selectedCityId)) || sortedCities[0];
  }, [sortedCities, selectedCityId]);

  const selectedCityConfig = useMemo(() => {
    if (!selectedCity) return null;
    return cityConfigById.get(Number(selectedCity.id)) || null;
  }, [cityConfigById, selectedCity]);

  const selectedMapCityEntry = useMemo(() => {
    if (!configuredCityEntries.length) return null;
    return configuredCityEntries.find((entry) => Number(entry.city.id) === Number(selectedMapCityId)) || configuredCityEntries[0] || null;
  }, [configuredCityEntries, selectedMapCityId]);

  const selectedMapCity = selectedMapCityEntry?.city || null;
  const selectedMapCityConfig = selectedMapCityEntry?.configEntry || null;

  const buildCityFormFromConfig = (cityId, entry) => ({
    city_id: String(cityId),
    dome_theme: entry?.config?.dome_theme || 'neutral',
    background_image: entry?.config?.background_image || '',
    metadata: JSON.stringify(entry?.config?.metadata || {}, null, 2)
  });

  useEffect(() => {
    if (!sortedCities.length) {
      setSelectedCityId('');
      setCityConfigForm(emptyCityConfigForm());
      return;
    }

    const currentExists = sortedCities.some((city) => Number(city.id) === Number(selectedCityId));
    const nextCity = currentExists
      ? sortedCities.find((city) => Number(city.id) === Number(selectedCityId))
      : sortedCities[0];

    if (!currentExists && nextCity) {
      setSelectedCityId(String(nextCity.id));
    }

    if (nextCity) {
      const entry = cityConfigById.get(Number(nextCity.id));
      setCityConfigForm(entry ? buildCityFormFromConfig(nextCity.id, entry) : emptyCityConfigForm(nextCity.id));
    }
  }, [cityConfigById, selectedCityId, sortedCities]);

  useEffect(() => {
    if (!configuredCityEntries.length) {
      setSelectedMapCityId('');
      return;
    }

    const currentExists = configuredCityEntries.some((entry) => Number(entry.city.id) === Number(selectedMapCityId));
    const nextCity = currentExists
      ? configuredCityEntries.find((entry) => Number(entry.city.id) === Number(selectedMapCityId))?.city
      : configuredCityEntries[0]?.city;

    if (nextCity && String(nextCity.id) !== String(selectedMapCityId)) {
      setSelectedMapCityId(String(nextCity.id));
    }
  }, [configuredCityEntries, selectedMapCityId]);

  const editTileset = (tileset) => {
    const metadata = tileset.metadata && typeof tileset.metadata === 'object' ? tileset.metadata : {};
    setTilesetForm({
      id: tileset.id,
      slug: tileset.slug || '',
      name: tileset.name || '',
      category: tileset.category || 'terrain',
      image: tileset.image || '',
      tile_size: Number(tileset.tile_size) || 48,
      columns_count: Number(tileset.columns_count) || 0,
      metadata: JSON.stringify({
        ...metadata,
        catalog: Array.isArray(tileset.catalog) ? tileset.catalog : Array.isArray(metadata.catalog) ? metadata.catalog : []
      }, null, 2),
      catalog: Array.isArray(tileset.catalog)
        ? tileset.catalog.map(normalizeCatalogEntry)
        : Array.isArray(metadata.catalog)
          ? metadata.catalog.map(normalizeCatalogEntry)
          : [],
      is_active: !!tileset.is_active
    });
    setActiveSection('tilesets');
  };

  const generateTilesetCatalog = async () => {
    const imageSrc = resolveImageSrc(tilesetForm.image);
    if (!imageSrc) return;

    const { width, height } = await loadImageSize(imageSrc);
    const generated = buildAutoCatalogEntries({
      name: tilesetForm.name || 'Tile',
      category: tilesetForm.category || 'general',
      tileSize: Number(tilesetForm.tile_size) || 48,
      columnsCount: Number(tilesetForm.columns_count) || 0,
      imageWidth: width,
      imageHeight: height
    });

    setTilesetForm((current) => ({
      ...current,
      catalog: generated
    }));
  };

  const reclassifyTilesetCatalog = () => {
    const currentCatalog = Array.isArray(tilesetForm.catalog) ? tilesetForm.catalog : [];
    if (!currentCatalog.length) return;

    const total = currentCatalog.length;
    const baseCategory = String(tilesetForm.category || 'general').trim() || 'general';
    const nextCatalog = currentCatalog.map((entry, index) => ({
      ...normalizeCatalogEntry(entry),
      category: inferCatalogCategory(index, total, baseCategory)
    }));

    setTilesetForm((current) => ({
      ...current,
      catalog: nextCatalog
    }));
  };

  const clearTilesetCatalog = () => {
    setTilesetForm((current) => ({
      ...current,
      catalog: []
    }));
  };

  const moveCatalogEntry = (index, direction) => {
    setTilesetForm((current) => {
      const next = Array.isArray(current.catalog) ? [...current.catalog] : [];
      const target = index + direction;
      if (index < 0 || target < 0 || index >= next.length || target >= next.length) {
        return current;
      }
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item);
      return { ...current, catalog: next };
    });
  };

  const cleanTilesetCatalog = () => {
    setTilesetForm((current) => {
      const normalized = dedupeCatalogEntries((Array.isArray(current.catalog) ? current.catalog : []).map(normalizeCatalogEntry));
      return {
        ...current,
        catalog: sortCatalogEntries(normalized)
      };
    });
  };

  const selectCity = (city) => {
    const cityId = String(city.id);
    setSelectedCityId(cityId);
    const entry = cityConfigById.get(Number(city.id));
    setCityConfigForm(entry ? buildCityFormFromConfig(city.id, entry) : emptyCityConfigForm(city.id));
    setActiveSection('cities');
  };

  const submitTileset = (e) => {
    e.preventDefault();
    onSaveTileset(normalizeTilesetForm(tilesetForm));
    setTilesetForm(emptyTilesetForm());
  };

  const submitCity = (e) => {
    e.preventDefault();
    const selectedCityValue = Number(cityConfigForm.city_id || selectedCityId);
    if (!selectedCityValue) return;
    onSaveCity(selectedCityValue, normalizeCityConfigForm(cityConfigForm));
  };

  return (
    <div className="space-y-6">
      <section className="bg-[#11132d] p-6 rounded-3xl border border-slate-800 shadow-xl space-y-5">
        <div className="flex items-start justify-between gap-4 border-b border-slate-800 pb-4">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-fuchsia-300">
              <Layers size={12} /> RPG Independiente
            </div>
            <h2 className="text-2xl font-bold text-white">Tilesets, NPCs y configuracion RPG</h2>
            <p className="text-sm text-slate-400 max-w-2xl">
              Este bloque no usa el editor de mapas. Aqui gestionas multiples tilesets, NPCs y el perfil RPG de cada ciudad real del admin.
            </p>
          </div>
          <div className="flex gap-2 rounded-2xl border border-slate-800 bg-slate-950/60 p-1">
            <button
              type="button"
              onClick={() => setActiveSection('tilesets')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${activeSection === 'tilesets' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Tilesets
            </button>
            <button
              type="button"
              onClick={() => setActiveSection('cities')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${activeSection === 'cities' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Ciudades
            </button>
            <button
              type="button"
              onClick={() => setActiveSection('map')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${activeSection === 'map' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Mapa
            </button>
            <button
              type="button"
              onClick={() => setActiveSection('crops')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${activeSection === 'crops' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'}`}
            >
              Cultivos
            </button>
            <button
              type="button"
              onClick={() => setActiveSection('npcs')}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${activeSection === 'npcs' ? 'bg-game-primary text-white' : 'text-slate-400 hover:text-white'}`}
            >
              NPCs
            </button>
          </div>
        </div>

      </section>

      {activeSection === 'tilesets' && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/30">
              <div>
                <h3 className="font-bold text-white">Biblioteca de tilesets</h3>
                <p className="text-xs text-slate-500">Multiples tilesets para suelos, objetos y decoracion</p>
              </div>
            </div>
            <div className="divide-y divide-slate-800/70">
              {sortedTilesets.length === 0 ? (
                <div className="p-8 text-sm text-slate-500">Todavia no hay tilesets RPG creados.</div>
              ) : sortedTilesets.map((tileset) => (
                <div key={tileset.id} className="p-4 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden flex items-center justify-center shrink-0">
                      {tileset.image ? <img src={resolveImageSrc(tileset.image)} alt={tileset.name} className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-slate-500" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-bold text-white truncate">{tileset.name}</h4>
                        <span className="rounded-full border border-slate-700 bg-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">{tileset.category}</span>
                        {!tileset.is_active && (
                          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300">Inactivo</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate max-w-xl">Slug: {tileset.slug} · Tile size: {tileset.tile_size} · Columnas: {tileset.columns_count}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPreviewTileset(tileset)}
                      className="rounded-lg bg-slate-800 p-2 text-slate-400 hover:text-cyan-300 disabled:opacity-40"
                      disabled={!tileset.image}
                      title="Ver imagen"
                    >
                      <Eye size={15} />
                    </button>
                    <button type="button" onClick={() => editTileset(tileset)} className="rounded-lg bg-slate-800 p-2 text-slate-400 hover:text-white">
                      <Edit2 size={15} />
                    </button>
                    <button type="button" onClick={() => onDeleteTileset(tileset.id)} className="rounded-lg bg-slate-800 p-2 text-slate-400 hover:text-rose-400">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl p-6 space-y-5">
            <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
              <Layers size={22} className="text-game-primary" />
              <div>
                <h3 className="font-bold text-white">{tilesetForm.id ? 'Editar tileset' : 'Crear tileset'}</h3>
                <p className="text-xs text-slate-500">Sube o escribe el sprite sheet y define su tamano.</p>
              </div>
            </div>
            <form onSubmit={submitTileset} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Nombre</label>
                <input value={tilesetForm.name} onChange={(e) => setTilesetForm((current) => ({ ...current, name: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Slug</label>
                  <input value={tilesetForm.slug} onChange={(e) => setTilesetForm((current) => ({ ...current, slug: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Categoria</label>
                  <select value={tilesetForm.category} onChange={(e) => setTilesetForm((current) => ({ ...current, category: e.target.value }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none">
                    {TILESET_CATEGORIES.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Imagen</label>
                <div className="flex gap-3">
                  <input value={tilesetForm.image} onChange={(e) => setTilesetForm((current) => ({ ...current, image: e.target.value }))} className="flex-1 rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" placeholder="rpg-tiles.png" />
                  <button
                    type="button"
                    onClick={() => onOpenImageGallery?.('tileset')}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-white hover:border-game-primary hover:bg-slate-800"
                  >
                    <ImageIcon size={16} /> Galeria
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Tamano tile</label>
                  <input type="number" value={tilesetForm.tile_size} onChange={(e) => setTilesetForm((current) => ({ ...current, tile_size: Number(e.target.value) || 48 }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Columnas</label>
                  <input type="number" value={tilesetForm.columns_count} onChange={(e) => setTilesetForm((current) => ({ ...current, columns_count: Number(e.target.value) || 0 }))} className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Metadata JSON</label>
                <textarea value={tilesetForm.metadata} onChange={(e) => setTilesetForm((current) => ({ ...current, metadata: e.target.value }))} className="h-28 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none font-mono text-xs" />
              </div>
              <div className="space-y-3 rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Catalogo de tiles</div>
                    <p className="text-[11px] text-slate-500">Nombres, categorias, preview y bloqueo por tile.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={generateTilesetCatalog}
                      disabled={!tilesetForm.image}
                      className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs font-bold text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-40"
                    >
                      Generar desde imagen
                    </button>
                    <button
                      type="button"
                      onClick={clearTilesetCatalog}
                      className="rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold text-white hover:border-game-primary"
                    >
                      Vaciar catalogo
                    </button>
                    <button
                      type="button"
                      onClick={reclassifyTilesetCatalog}
                      disabled={!Array.isArray(tilesetForm.catalog) || tilesetForm.catalog.length === 0}
                      className="rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold text-white hover:border-game-primary disabled:opacity-40"
                    >
                      Reclasificar
                    </button>
                    <button
                      type="button"
                      onClick={cleanTilesetCatalog}
                      disabled={!Array.isArray(tilesetForm.catalog) || tilesetForm.catalog.length === 0}
                      className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs font-bold text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40"
                    >
                      Limpiar y ordenar
                    </button>
                    <button
                      type="button"
                      onClick={() => setTilesetForm((current) => ({
                        ...current,
                        catalog: [...(Array.isArray(current.catalog) ? current.catalog : []), normalizeCatalogEntry({ name: '', category: 'general', tile_index: 0, blocked: 1, description: '' })]
                      }))}
                      className="rounded-xl border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs font-bold text-white hover:border-game-primary"
                    >
                      + Anadir tile
                    </button>
                  </div>
                </div>
                <div className="space-y-4 max-h-80 overflow-auto custom-scrollbar pr-1">
                  {(Array.isArray(tilesetForm.catalog) ? tilesetForm.catalog : []).length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-4 text-sm text-slate-500">
                      Aun no hay tiles en el catalogo. Usa el editor para crear los tiles por nombre.
                    </div>
                  ) : (
                    (() => {
                      const catalog = Array.isArray(tilesetForm.catalog) ? tilesetForm.catalog : [];
                      const grouped = catalog.reduce((acc, entry, index) => {
                        const category = String(entry.category || 'general').trim() || 'general';
                        if (!acc[category]) acc[category] = [];
                        acc[category].push({ entry, index });
                        return acc;
                      }, {});
                      const order = CATALOG_CATEGORIES.map((item) => item.value);
                      const groupKeys = [
                        ...order.filter((key) => grouped[key]?.length),
                        ...Object.keys(grouped).filter((key) => !order.includes(key)).sort()
                      ];

                      return groupKeys.map((categoryKey) => (
                        <div key={categoryKey} className="rounded-2xl border border-slate-800 bg-slate-900/30 p-3 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
                                {CATALOG_CATEGORIES.find((item) => item.value === categoryKey)?.label || categoryKey}
                              </div>
                              <div className="text-[11px] text-slate-500">{grouped[categoryKey].length} tiles</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setTilesetForm((current) => ({
                                ...current,
                                catalog: current.catalog.map((item) => (
                                  String(item.category || 'general').trim() === categoryKey
                                    ? { ...item, category: categoryKey }
                                    : item
                                ))
                              }))}
                              className="rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-[11px] font-bold text-slate-300 hover:border-game-primary hover:text-white"
                            >
                              Normalizar grupo
                            </button>
                          </div>
                          <div className="space-y-3">
                            {grouped[categoryKey].map(({ entry, index }) => {
                              const tileSize = Number(tilesetForm.tile_size) || 48;
                              const columnsCount = Number(tilesetForm.columns_count) || 1;
                              const sourceX = (Number(entry.tile_index) % Math.max(1, columnsCount)) * tileSize;
                              const sourceY = Math.floor(Number(entry.tile_index) / Math.max(1, columnsCount)) * tileSize;
                              const apiBaseUrl = import.meta.env.VITE_API_URL || '';
                              const imageSrc = tilesetForm.image
                                ? (tilesetForm.image.startsWith('http://') || tilesetForm.image.startsWith('https://') || tilesetForm.image.startsWith('/') ? tilesetForm.image : `${apiBaseUrl}/uploads/gallery/${tilesetForm.image}`)
                                : '';
                              return (
                                <div key={`${index}-${entry.tile_index}`} className="rounded-2xl border border-slate-800 bg-slate-900/50 p-3 space-y-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Tile #{index + 1}</div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => moveCatalogEntry(index, -1)}
                                        disabled={index === 0}
                                        className="rounded-lg border border-slate-700 bg-slate-950/60 p-1 text-slate-400 hover:border-game-primary hover:text-white disabled:opacity-40"
                                        title="Mover arriba"
                                      >
                                        ↑
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => moveCatalogEntry(index, 1)}
                                        disabled={index === (Array.isArray(tilesetForm.catalog) ? tilesetForm.catalog.length - 1 : 0)}
                                        className="rounded-lg border border-slate-700 bg-slate-950/60 p-1 text-slate-400 hover:border-game-primary hover:text-white disabled:opacity-40"
                                        title="Mover abajo"
                                      >
                                        ↓
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setTilesetForm((current) => ({
                                          ...current,
                                          catalog: current.catalog.filter((_, idx) => idx !== index)
                                        }))}
                                        className="rounded-lg border border-slate-700 bg-slate-950/60 p-1 text-rose-400 hover:border-rose-400 hover:text-rose-300"
                                        title="Eliminar tile"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-[84px_1fr] gap-3 items-start">
                                    <div className="rounded-xl border border-slate-700 bg-slate-950/70 p-2">
                                      <div
                                        className="h-16 w-16 rounded-lg border border-slate-700 bg-slate-900 bg-no-repeat"
                                        style={{
                                          backgroundImage: imageSrc ? `url(${imageSrc})` : 'none',
                                          backgroundPosition: `-${sourceX}px -${sourceY}px`,
                                          backgroundSize: `${Math.max(1, columnsCount) * 64}px auto`
                                        }}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <input
                                        value={entry.name}
                                        onChange={(e) => {
                                          const next = [...tilesetForm.catalog];
                                          next[index] = { ...next[index], name: e.target.value };
                                          setTilesetForm((current) => ({ ...current, catalog: next }));
                                        }}
                                        placeholder="Nombre del tile"
                                        className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                                      />
                                      <div className="grid grid-cols-2 gap-2">
                                        <select
                                          value={entry.category}
                                          onChange={(e) => {
                                            const next = [...tilesetForm.catalog];
                                            next[index] = { ...next[index], category: e.target.value };
                                            setTilesetForm((current) => ({ ...current, catalog: next }));
                                          }}
                                          className="rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                                        >
                                          {CATALOG_CATEGORIES.map((categoryItem) => (
                                            <option key={categoryItem.value} value={categoryItem.value}>{categoryItem.label}</option>
                                          ))}
                                        </select>
                                        <input
                                          type="number"
                                          value={entry.tile_index}
                                          onChange={(e) => {
                                            const next = [...tilesetForm.catalog];
                                            next[index] = { ...next[index], tile_index: Number(e.target.value) || 0 };
                                            setTilesetForm((current) => ({ ...current, catalog: next }));
                                          }}
                                          className="rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                                          placeholder="Indice"
                                        />
                                      </div>
                                      <textarea
                                        value={entry.description}
                                        onChange={(e) => {
                                          const next = [...tilesetForm.catalog];
                                          next[index] = { ...next[index], description: e.target.value };
                                          setTilesetForm((current) => ({ ...current, catalog: next }));
                                        }}
                                        placeholder="Descripcion"
                                        className="h-20 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none text-sm"
                                      />
                                      <label className="flex items-center justify-between rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-300">
                                        <span>Bloqueante</span>
                                        <input
                                          type="checkbox"
                                          checked={!!entry.blocked}
                                          onChange={(e) => {
                                            const next = [...tilesetForm.catalog];
                                            next[index] = { ...next[index], blocked: e.target.checked ? 1 : 0 };
                                            setTilesetForm((current) => ({ ...current, catalog: next }));
                                          }}
                                          className="h-4 w-4 accent-cyan-500"
                                        />
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()
                  )}
                </div>
              </div>
              <label className="flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-300">
                <input type="checkbox" checked={tilesetForm.is_active} onChange={(e) => setTilesetForm((current) => ({ ...current, is_active: e.target.checked }))} className="h-4 w-4 accent-cyan-500" />
                Activo
              </label>
              <div className="flex gap-3">
                <button type="submit" disabled={isSavingTileset} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-game-primary px-4 py-3 font-bold text-white hover:bg-game-primary/80 disabled:opacity-50">
                  <Save size={16} /> {isSavingTileset ? 'Guardando...' : (tilesetForm.id ? 'Actualizar tileset' : 'Crear tileset')}
                </button>
                <button type="button" onClick={() => setTilesetForm(emptyTilesetForm())} className="rounded-xl border border-slate-700 px-4 py-3 font-bold text-slate-300 hover:border-slate-500 hover:text-white">
                  Limpiar
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {activeSection === 'cities' && (
        <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_0.85fr] gap-6">
          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/30">
              <div>
                <h3 className="font-bold text-white">Ciudades base del admin</h3>
                <p className="text-xs text-slate-500">Selecciona una ciudad real y guardale su perfil RPG</p>
              </div>
            </div>
            <div className="divide-y divide-slate-800/70">
              {sortedCities.length === 0 ? (
                <div className="p-8 text-sm text-slate-500">No hay ciudades creadas en el admin.</div>
              ) : sortedCities.map((city) => {
                const cityConfig = cityConfigById.get(Number(city.id));
                const hasRpgConfig = !!cityConfig?.config;
                return (
                  <button
                    key={city.id}
                    type="button"
                    onClick={() => selectCity(city)}
                    className={`w-full text-left p-4 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors ${Number(selectedCityId) === Number(city.id) ? 'bg-white/5' : ''}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden flex items-center justify-center shrink-0">
                        {city.image ? <img src={resolveImageSrc(city.image)} alt={city.name} className="w-full h-full object-cover" /> : <Castle size={20} className="text-slate-500" />}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-bold text-white truncate">{city.name}</h4>
                          <span className="rounded-full border border-slate-700 bg-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">
                            {getCityTypeLabel(city.city_type)}
                          </span>
                          <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${hasRpgConfig ? 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border border-slate-700 bg-slate-900 text-slate-400'}`}>
                            {hasRpgConfig ? 'Con RPG' : 'Sin RPG'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 truncate max-w-xl">
                          Sector: {city.id_sector ?? '-'} · Coord: {city.coordinate ?? '-'} · Clan: {city.clan ?? 0}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                      <Edit2 size={15} />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl p-6 space-y-5">
            <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
              <Castle size={22} className="text-game-primary" />
              <div>
                <h3 className="font-bold text-white">{selectedCity ? `Configurar ${selectedCity.name}` : 'Configurar ciudad RPG'}</h3>
                <p className="text-xs text-slate-500">La ciudad ya existe en el admin; aqui solo guardas su perfil RPG.</p>
              </div>
            </div>

            {!selectedCity ? (
              <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/40 p-6 text-sm text-slate-500">
                No hay ciudades disponibles para configurar.
              </div>
            ) : (
              <form onSubmit={submitCity} className="space-y-4">
                <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4 space-y-2">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Ciudad base</div>
                  <div className="text-lg font-bold text-white">{selectedCity.name}</div>
                  <div className="text-xs text-slate-400">
                    Sector: {selectedCity.id_sector ?? '-'} · Coord: {selectedCity.coordinate ?? '-'} · Clan: {selectedCity.clan ?? 0}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Ciudad</label>
                  <select
                    value={cityConfigForm.city_id}
                    onChange={(e) => {
                      const nextCityId = e.target.value;
                      setSelectedCityId(nextCityId);
                      const entry = cityConfigById.get(Number(nextCityId));
                      setCityConfigForm(entry ? buildCityFormFromConfig(nextCityId, entry) : emptyCityConfigForm(nextCityId));
                    }}
                    className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                  >
                    <option value="">Selecciona una ciudad</option>
                    {sortedCities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Tema de cupula</label>
                    <input
                      value={cityConfigForm.dome_theme}
                      onChange={(e) => setCityConfigForm((current) => ({ ...current, dome_theme: e.target.value }))}
                      className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                      placeholder="neon, bosque, industrial..."
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Imagen fondo</label>
                    <div className="flex gap-3">
                      <input
                        value={cityConfigForm.background_image}
                        onChange={(e) => setCityConfigForm((current) => ({ ...current, background_image: e.target.value }))}
                        className="flex-1 rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                        placeholder="city-rpg.png"
                      />
                      <button
                        type="button"
                        onClick={() => onOpenImageGallery?.('city-config')}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-3 text-sm font-bold text-white hover:border-game-primary hover:bg-slate-800"
                      >
                        <ImageIcon size={16} /> Galeria
                      </button>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Metadata JSON</label>
                  <textarea
                    value={cityConfigForm.metadata}
                    onChange={(e) => setCityConfigForm((current) => ({ ...current, metadata: e.target.value }))}
                    className="h-24 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none font-mono text-xs"
                  />
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-400">
                  Los tilesets RPG ahora son globales. Cualquier tileset activo que crees estara disponible automaticamente en todas las ciudades.
                </div>
                <div className="flex gap-3">
                  <button type="submit" disabled={isSavingCity} className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-game-primary px-4 py-3 font-bold text-white hover:bg-game-primary/80 disabled:opacity-50">
                    <Save size={16} /> {isSavingCity ? 'Guardando...' : 'Guardar perfil RPG'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const resetCityId = selectedCity ? String(selectedCity.id) : '';
                      setSelectedCityId(resetCityId);
                      setCityConfigForm(emptyCityConfigForm(resetCityId));
                    }}
                    className="rounded-xl border border-slate-700 px-4 py-3 font-bold text-slate-300 hover:border-slate-500 hover:text-white"
                  >
                    Limpiar
                  </button>
                  {selectedCityConfig?.config && (
                    <button
                      type="button"
                      onClick={() => onDeleteCity(selectedCity.id)}
                      className="rounded-xl border border-rose-500/30 px-4 py-3 font-bold text-rose-300 hover:border-rose-400 hover:text-rose-200"
                    >
                      Eliminar perfil
                    </button>
                  )}
                </div>
              </form>
            )}
          </section>
        </div>
      )}

      {activeSection === 'map' && (
        <div className="space-y-6">
          <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl p-6 space-y-5">
            <div className="flex items-start justify-between gap-4 border-b border-slate-800 pb-4">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-cyan-300">
                  <MapPinned size={12} /> Mapa de la Cúpula
                </div>
                <h3 className="text-2xl font-bold text-white">Editor visual del interior urbano</h3>
                  <p className="text-sm text-slate-400 max-w-3xl">
                  Pinta el mapa por tiles y capas usando los tilesets RPG globales. Este mapa no toca el editor de sectores.
                </p>
              </div>
              <div className="min-w-[280px]">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Ciudad a editar</label>
                <select
                  value={selectedMapCityId}
                  onChange={(e) => {
                    const nextCityId = e.target.value;
                    setSelectedMapCityId(nextCityId);
                  }}
                  className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
                >
                  <option value="">Selecciona una ciudad con perfil RPG</option>
                  {configuredCities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <RpgCityMapEditor
            city={selectedMapCity}
            config={selectedMapCityConfig?.config}
            onSaveMap={onSaveCityMap}
            isSaving={isSavingCityMap}
          />
        </div>
      )}

      {activeSection === 'crops' && (
        <RpgCropManager
          transformations={cropTransformations}
          items={items}
          isSaving={isSavingCropTransformation}
          onSaveTransformation={onSaveCropTransformation}
          onDeleteTransformation={onDeleteCropTransformation}
        />
      )}

      {activeSection === 'npcs' && (
        <RpgNpcManager
          ref={npcManagerRef}
          templates={npcTemplates}
          instances={npcInstances}
          cities={cities}
          playerAppearance={playerAppearance}
          isSavingTemplate={isSavingNpcTemplate}
          isSavingInstance={isSavingNpcInstance}
          isSavingPlayerAppearance={isSavingPlayerAppearance}
          onOpenImageGallery={onOpenImageGallery}
          onSaveTemplate={onSaveNpcTemplate}
          onDeleteTemplate={onDeleteNpcTemplate}
          onSaveInstance={onSaveNpcInstance}
          onDeleteInstance={onDeleteNpcInstance}
          onSavePlayerAppearance={onSavePlayerAppearance}
        />
      )}

      {previewTileset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-5xl rounded-3xl border border-slate-700 bg-[#0d1028] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between gap-4 p-5 border-b border-slate-800">
              <div>
                <h3 className="text-lg font-bold text-white">{previewTileset.name}</h3>
                <p className="text-xs text-slate-500">Tileset RPG · {previewTileset.slug}</p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewTileset(null)}
                className="rounded-xl bg-slate-800 p-2 text-slate-400 hover:text-white"
                title="Cerrar"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="overflow-auto rounded-2xl border border-slate-800 bg-slate-950/60">
                {previewTileset.image ? (
                  <img
                    src={resolveImageSrc(previewTileset.image)}
                    alt={previewTileset.name}
                    className="max-h-[75vh] w-full object-contain bg-slate-950"
                  />
                ) : (
                  <div className="flex h-64 items-center justify-center text-slate-500">
                    Este tileset no tiene imagen asignada.
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                <span className="rounded-full border border-slate-700 px-3 py-1">Categoria: {previewTileset.category}</span>
                <span className="rounded-full border border-slate-700 px-3 py-1">Tile size: {previewTileset.tile_size}</span>
                <span className="rounded-full border border-slate-700 px-3 py-1">Columnas: {previewTileset.columns_count}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default forwardRef(RpgWorldManager);
